import { ConfigurableProductProductPriceTableEntryTypeEnum, type ConfigurableProductProductObject, type ProductDataObject } from '@/types/generated';
import _ from 'lodash';

type SizeDimensions = {
    shortSide: number;
    longSide: number;
};

const SIZE_LABEL_PATTERN = /(\d+(?:[.,]\d+)?)(\s*[x×]\s*)(\d+(?:[.,]\d+)?)/i;
const FIRST_NUMBER_PATTERN = /\d+(?:[.,]\d+)?/;

function normalizeSizeDimensions(width: number, height: number): SizeDimensions {
    return {
        shortSide: Math.min(width, height),
        longSide: Math.max(width, height),
    };
}

function parseSizeValue(rawValue: string): number | null {
    const value = Number(rawValue.replace(',', '.'));

    if (!Number.isFinite(value)) {
        return null;
    }

    return value;
}

function getProductLabel(configurableProductProduct: ConfigurableProductProductObject): string {
    return configurableProductProduct.metadata.price_table_entry?.product_label ?? '';
}

function isNumber(value: unknown): value is number {
    return typeof value === 'number' && Number.isFinite(value);
}

function getAmountUniqueKey(productLabel: string, productSku: string): string {
    if (productLabel) {
        return `amount:${productLabel}`;
    }

    return `amount:${productSku}`;
}

function getSizeUniqueKey(configurableProductProduct: ConfigurableProductProductObject): string {
    const { width, height } = configurableProductProduct.product.data;

    if (isNumber(width) && isNumber(height)) {
        return `size:${width}x${height}`;
    }

    return `size:${getProductLabel(configurableProductProduct) || configurableProductProduct.product.sku}`;
}

function parseFirstNumber(label: string): number | null {
    const match = label.match(FIRST_NUMBER_PATTERN);

    if (!match) {
        return null;
    }

    return parseSizeValue(match[0]);
}

function getDefaultSortKey(productLabel: string): [number, number, string] {
    const numericValue = parseFirstNumber(productLabel);

    if (numericValue !== null) {
        return [numericValue, numericValue, productLabel];
    }

    return [Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, productLabel];
}

function getAmountSortKey(productLabel: string): [number, number, string] {
    const amount = Number(productLabel);

    if (!Number.isFinite(amount)) {
        return getDefaultSortKey(productLabel);
    }

    return [amount, amount, productLabel];
}

export function parseDimensionsFromLabel(label: string | null | undefined): SizeDimensions | null {
    if (!label) {
        return null;
    }

    const matches = label.match(SIZE_LABEL_PATTERN);

    if (!matches) {
        return null;
    }

    const firstValue = parseSizeValue(matches[1]);
    const secondValue = parseSizeValue(matches[3]);

    if (firstValue === null || secondValue === null) {
        return null;
    }

    return normalizeSizeDimensions(firstValue, secondValue);
}

function hasNameBasedLabel(configurableProductProduct: ConfigurableProductProductObject): boolean {
    const productLabel = getProductLabel(configurableProductProduct);

    return productLabel !== '' && parseDimensionsFromLabel(productLabel) === null;
}

export function isAmountPriceTableEntry(configurableProductProduct: ConfigurableProductProductObject): boolean {
    return configurableProductProduct.metadata.price_table_entry?.type === ConfigurableProductProductPriceTableEntryTypeEnum.AMOUNT;
}

export function getSizeDimensions(configurableProductProduct: ConfigurableProductProductObject): SizeDimensions | null {
    const { width, height } = configurableProductProduct.product.data;

    if (isNumber(width) && isNumber(height)) {
        return normalizeSizeDimensions(width, height);
    }

    return parseDimensionsFromLabel(getProductLabel(configurableProductProduct));
}

export function getPriceTableUniqueKey(configurableProductProduct: ConfigurableProductProductObject): string {
    const productLabel = getProductLabel(configurableProductProduct);

    if (isAmountPriceTableEntry(configurableProductProduct)) {
        return getAmountUniqueKey(productLabel, configurableProductProduct.product.sku);
    }

    if (hasNameBasedLabel(configurableProductProduct)) {
        return `size:${productLabel}`;
    }

    return getSizeUniqueKey(configurableProductProduct);
}

export function getPriceTableSortKey(configurableProductProduct: ConfigurableProductProductObject): [number, number, string] {
    const productLabel = getProductLabel(configurableProductProduct);

    if (isAmountPriceTableEntry(configurableProductProduct)) {
        return getAmountSortKey(productLabel);
    }

    if (hasNameBasedLabel(configurableProductProduct)) {
        return getDefaultSortKey(productLabel);
    }

    const sizeDimensions = getSizeDimensions(configurableProductProduct);

    if (sizeDimensions) {
        return [sizeDimensions.longSide, sizeDimensions.shortSide, productLabel];
    }

    return getDefaultSortKey(productLabel);
}

export function getPriceTableDisplayLabel(configurableProductProduct: ConfigurableProductProductObject): string | undefined {
    const productLabel = getProductLabel(configurableProductProduct);

    if (productLabel) {
        return productLabel;
    }

    const { width, height } = configurableProductProduct.product.data;

    return isNumber(width) && isNumber(height) ? `${width}x${height}` : undefined;
}

/** Rows matching every active configured filter, or all rows when none match so the table is never empty. */
export function filterPriceTableProducts(
    products: ConfigurableProductProductObject[],
    filterKeys: string[] | null | undefined,
    activeAttributeValues: Record<string, string>,
): ConfigurableProductProductObject[] {
    const activeFilters = _.pick(activeAttributeValues, filterKeys ?? []);

    if (_.isEmpty(activeFilters)) {
        return products;
    }

    const matchingProducts = _.filter(products, (configurableProductProduct: ConfigurableProductProductObject) =>
        _.every(
            activeFilters,
            (value: string, key: string) => _.toString(configurableProductProduct.product.data[key as keyof ProductDataObject]) === value,
        ),
    );

    return matchingProducts.length > 0 ? matchingProducts : products;
}

export type PriceTableRow = {
    configurableProductProduct: ConfigurableProductProductObject;
    label: string | undefined;
};

function pricesOf(
    configurableProductProduct: ConfigurableProductProductObject,
): [ProductDataObject['product_price'], ProductDataObject['product_price_recommended']] {
    const { product_price: price, product_price_recommended: recommendedPrice } = configurableProductProduct.product.data;

    return [price, recommendedPrice];
}

/** When every size costs the same, one row with the all sizes label replaces the list. */
export function collapsePriceTableRowsWithSamePrice(products: ConfigurableProductProductObject[], allSizesLabel: string): PriceTableRow[] {
    const first = _.first(products);

    if (
        first &&
        products.length > 1 &&
        _.every(products, (product: ConfigurableProductProductObject) => _.isEqual(pricesOf(product), pricesOf(first)))
    ) {
        return [{ configurableProductProduct: first, label: allSizesLabel }];
    }

    return _.map(products, (configurableProductProduct: ConfigurableProductProductObject) => ({
        configurableProductProduct,
        label: getPriceTableDisplayLabel(configurableProductProduct),
    }));
}
